import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { getApiBaseUrl, publicMediaSrc } from "../config/api";
import CategoryCard, { productHref, MEDIA_CARD_CLASS, MEDIA_CARD_GUTTER, MEDIA_CARD_GRID } from "../components/CategoryCard";
import ProductsSeo, { productCardImageAlt } from "../components/ProductsSeo";
import allProductsBg from "../assets/all-products-bg.jpg";
import shieldIcon from "../assets/all-products-shield.png";
import warehouseIcon from "../assets/all-products-warehouse.png";

const PAGE_GUTTER = "px-16 md:px-32 lg:px-40";
const PAGE_SIZE = 12;
const SORTS = new Set(["newest", "oldest", "name-asc", "name-desc"]);

function lockDoubleClick(event) {
  const el = event.currentTarget;
  if (el.dataset.navLock === "1") {
    event.preventDefault();
    return;
  }
  el.dataset.navLock = "1";
  window.setTimeout(() => {
    delete el.dataset.navLock;
  }, 700);
}

function prefersReducedMotion() {
  return typeof window !== "undefined" && window.matchMedia("(prefers-reduced-motion: reduce)").matches;
}

function categorySlugOf(item) {
  return String(item?.category?.slug || "").trim();
}

function categoryNameOf(item) {
  return String(item?.category?.categoryName || "").trim();
}

function normalizeQuery(value) {
  return String(value || "").trim().replace(/\s+/g, " ");
}

function parseSort(value) {
  const raw = String(value || "").trim();
  if (raw === "name-az") return "name-asc";
  if (raw === "name-za") return "name-desc";
  if (SORTS.has(raw)) return raw;
  return "newest";
}

function parsePage(value) {
  const page = Number.parseInt(String(value || "1"), 10);
  if (!Number.isFinite(page) || page < 1) return 1;
  return page;
}

function matchesProduct(item, rawQuery) {
  const needle = normalizeQuery(rawQuery).toLowerCase();
  if (!needle) return true;
  const fields = [
    item.productName,
    item.shortDescription,
    item.description,
    item.brand,
    item.sku,
    item.modelNumber,
    categoryNameOf(item),
    Array.isArray(item.searchTags) ? item.searchTags.join(" ") : "",
  ];
  return fields.some((field) => String(field || "").toLowerCase().includes(needle));
}

function sortProducts(list, sort) {
  const next = [...list];
  const time = (item) => {
    const date = new Date(item?.updatedAt || 0);
    return Number.isNaN(date.getTime()) ? 0 : date.getTime();
  };
  if (sort === "oldest") return next.sort((a, b) => time(a) - time(b));
  if (sort === "name-asc") {
    return next.sort((a, b) => String(a.productName || "").localeCompare(String(b.productName || "")));
  }
  if (sort === "name-desc") {
    return next.sort((a, b) => String(b.productName || "").localeCompare(String(a.productName || "")));
  }
  return next.sort((a, b) => time(b) - time(a));
}

function writeParams({ q, category, sort, page }, mode, setSearchParams) {
  const params = new URLSearchParams();
  const query = String(q || "");
  if (query) params.set("q", query);
  if (category) params.set("category", category);
  if (sort && sort !== "newest") params.set("sort", sort);
  if (page > 1) params.set("page", String(page));
  setSearchParams(params, { replace: mode === "replace" });
}

function ProductsPage() {
  const headingRef = useRef(null);
  const pageRef = useRef(null);
  const shouldScrollToHeading = useRef(false);
  const [searchParams, setSearchParams] = useSearchParams();
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [contact, setContact] = useState(null);
  const [contactStatus, setContactStatus] = useState("loading");
  const [productStatus, setProductStatus] = useState("loading");
  const [categoryStatus, setCategoryStatus] = useState("loading");
  const [pageBusy, setPageBusy] = useState(false);
  const [loadNonce, setLoadNonce] = useState(0);

  const query = searchParams.get("q") || "";
  const requestedCategory = String(searchParams.get("category") || "").trim();
  const sort = parseSort(searchParams.get("sort"));
  const requestedPage = parsePage(searchParams.get("page"));

  const loadLists = useCallback(() => {
    let cancelled = false;
    setProductStatus("loading");
    setCategoryStatus("loading");
    setProducts([]);
    setCategories([]);

    fetch(`${getApiBaseUrl()}/products`, { headers: { Accept: "application/json" } })
      .then((res) => {
        if (!res.ok) throw new Error("Unable to load products");
        return res.json();
      })
      .then((list) => {
        if (cancelled) return;
        setProducts((Array.isArray(list) ? list : []).filter((item) => item?.isPublished !== false));
        setProductStatus("ready");
      })
      .catch(() => {
        if (!cancelled) {
          setProducts([]);
          setProductStatus("error");
        }
      });

    fetch(`${getApiBaseUrl()}/categories`, { headers: { Accept: "application/json" } })
      .then((res) => {
        if (!res.ok) throw new Error("Unable to load categories");
        return res.json();
      })
      .then((list) => {
        if (cancelled) return;
        setCategories(
          (Array.isArray(list) ? list : [])
            .filter((item) => item?.isPublished !== false && item?.slug)
            .sort((a, b) => String(a.categoryName || "").localeCompare(String(b.categoryName || "")))
        );
        setCategoryStatus("ready");
      })
      .catch(() => {
        if (!cancelled) {
          setCategories([]);
          setCategoryStatus("error");
        }
      });

    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => loadLists(), [loadLists, loadNonce]);

  useEffect(() => {
    let cancelled = false;
    fetch(`${getApiBaseUrl()}/contacts`, { headers: { Accept: "application/json" } })
      .then((res) => (res.ok ? res.json() : []))
      .then((list) => {
        if (cancelled) return;
        setContact(Array.isArray(list) && list[0] ? list[0] : null);
        setContactStatus("ready");
      })
      .catch(() => {
        if (cancelled) return;
        setContact(null);
        setContactStatus("error");
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const validCategory = categories.some((item) => item.slug === requestedCategory) ? requestedCategory : "";
  const categorySlug = categoryStatus === "ready" ? validCategory : requestedCategory;

  const filtered = useMemo(() => {
    const byCategory = categorySlug
      ? products.filter((item) => categorySlugOf(item) === categorySlug)
      : products;
    const matched = byCategory.filter((item) => matchesProduct(item, query));
    return sortProducts(matched, sort);
  }, [products, query, categorySlug, sort]);

  const pageCount = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE) || 1);
  const currentPage = Math.min(requestedPage, pageCount);
  const visible = filtered.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);
  const zeroProducts = productStatus === "ready" && products.length === 0;
  const noFilterResults = productStatus === "ready" && products.length > 0 && filtered.length === 0;
  const categoryFilterEnabled = categoryStatus === "ready" && categories.length > 0 && !zeroProducts;

  useEffect(() => {
    if (productStatus !== "ready") return;
    const categoryReady = categoryStatus === "ready";
    const categoryOk = !categoryReady || requestedCategory === validCategory;
    const pageOk = requestedPage === currentPage;
    const extras = searchParams.get("page") === "1" || searchParams.get("sort") === "newest";
    if (pageOk && categoryOk && !extras) return;
    writeParams(
      { q: query, category: categoryReady ? validCategory : requestedCategory, sort, page: currentPage },
      "replace",
      setSearchParams
    );
  }, [
    productStatus,
    categoryStatus,
    requestedPage,
    currentPage,
    requestedCategory,
    validCategory,
    query,
    sort,
    searchParams,
    setSearchParams,
  ]);

  useEffect(() => {
    if (!shouldScrollToHeading.current) return undefined;
    shouldScrollToHeading.current = false;
    const node = headingRef.current;
    if (!node) return undefined;
    node.scrollIntoView({ behavior: prefersReducedMotion() ? "auto" : "smooth", block: "start" });
    node.focus({ preventScroll: true });
    const timer = window.setTimeout(() => setPageBusy(false), 350);
    return () => window.clearTimeout(timer);
  }, [currentPage]);

  const setQueryValue = (value, mode = "replace") => {
    writeParams({ q: value, category: categorySlug, sort, page: 1 }, mode, setSearchParams);
  };

  const setCategory = (slug) => {
    writeParams({ q: query, category: slug, sort, page: 1 }, "push", setSearchParams);
  };

  const setSortValue = (next) => {
    writeParams({ q: query, category: categorySlug, sort: parseSort(next), page: 1 }, "push", setSearchParams);
  };

  const goToPage = (next) => {
    if (pageBusy || next === currentPage || next < 1 || next > pageCount) return;
    setPageBusy(true);
    shouldScrollToHeading.current = true;
    writeParams({ q: query, category: categorySlug, sort, page: next }, "push", setSearchParams);
  };

  const clearFilters = () => {
    writeParams({ q: "", category: "", sort: "newest", page: 1 }, "push", setSearchParams);
  };

  const liveMessage =
    productStatus === "loading"
      ? "Loading products"
      : productStatus === "error"
        ? "Unable to load products"
        : `${filtered.length} products found.`;

  return (
    <div className="products-page" ref={pageRef}>
      <ProductsSeo
        contact={contact}
        contactStatus={contactStatus}
        productsReady={productStatus === "ready"}
        items={visible}
        itemCount={filtered.length}
        page={currentPage}
        pageSize={PAGE_SIZE}
      />
      <div className="sr-only" aria-live="polite">
        {liveMessage}
      </div>

      <section className="relative overflow-hidden bg-at-dark text-white">
        <img
          src={allProductsBg}
          alt=""
          className="hero-zoom-subtle pointer-events-none absolute inset-0 h-full w-full object-cover object-center"
        />
        <div className="pointer-events-none absolute inset-0 bg-black/60" />
        <div className={`relative z-10 flex min-h-[280px] flex-col justify-between py-10 md:min-h-[340px] md:py-12 ${PAGE_GUTTER}`}>
          <nav aria-label="Breadcrumb" className="hero-in hero-in-1 text-xs text-white/70">
            <Link to="/" className="hover:text-white">
              Home
            </Link>
            <span className="px-2 text-white/40">/</span>
            <span className="text-white">Products</span>
          </nav>
          <div className="max-w-xl">
            <p className="hero-in hero-in-1 mt-6 text-[11px] font-semibold uppercase tracking-[0.22em] text-at-gold">
              Our Products
            </p>
            <h1 className="hero-in hero-in-1 mt-2 font-serif text-3xl uppercase leading-tight md:text-5xl">
              All Products
            </h1>
            <p className="hero-in hero-in-2 mt-3 max-w-md text-sm leading-relaxed text-white/70">
              Browse our range of steel, hardware and industrial supplies for construction and business requirements.
            </p>
            <ul className="hero-in hero-in-3 mt-6 flex flex-col gap-3 text-xs text-white/80 sm:flex-row sm:gap-8">
              <li className="flex items-center gap-2">
                <img src={shieldIcon} alt="" width={28} height={28} className="h-7 w-7 object-contain" />
                Quality supplies for a stronger tomorrow
              </li>
              <li className="flex items-center gap-2">
                <img src={warehouseIcon} alt="" width={28} height={28} className="h-7 w-7 object-contain" />
                Supporting Malaysia’s industry together
              </li>
            </ul>
          </div>
        </div>
      </section>

      <section className="bg-white py-14">
        <div className={MEDIA_CARD_GUTTER}>
          <div className="flex flex-col gap-6 lg:flex-row lg:items-end lg:justify-between">
            <div>
              <h2
                ref={headingRef}
                tabIndex={-1}
                className="scroll-mt-24 font-serif text-3xl uppercase text-at-dark outline-none md:text-4xl"
              >
                Browse All Products
              </h2>
              <p className="mt-2 text-sm text-neutral-500">
                {productStatus === "loading" ? (
                  <span className="inline-block h-4 w-24 bg-neutral-200 align-middle" aria-hidden="true" />
                ) : productStatus === "error" ? (
                  "Unable to load products"
                ) : (
                  `${filtered.length} products`
                )}
              </p>
            </div>
            <div className="flex w-full flex-col gap-3 sm:flex-row sm:items-center lg:w-auto">
              {productStatus === "loading" ? (
                <>
                  <span className="block h-[42px] w-full rounded-full bg-neutral-200 sm:w-64" aria-hidden="true" />
                  <span className="block h-[42px] w-full rounded-full bg-neutral-200 sm:w-48" aria-hidden="true" />
                  <span className="block h-[42px] w-full rounded-full bg-neutral-200 sm:w-48" aria-hidden="true" />
                </>
              ) : (
                <>
                  <label className="relative block w-full sm:w-64">
                    <span className="sr-only">Search products</span>
                    <svg
                      viewBox="0 0 24 24"
                      className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-neutral-400"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      aria-hidden="true"
                    >
                      <circle cx="11" cy="11" r="7" />
                      <path d="M20 20l-3-3" />
                    </svg>
                    <input
                      type="search"
                      value={query}
                      onChange={(event) => setQueryValue(event.target.value)}
                      onKeyDown={(event) => {
                        if (event.key === "Escape" && query) {
                          event.preventDefault();
                          setQueryValue("");
                        }
                      }}
                      placeholder="Search products?"
                      className={`category-search w-full rounded-full border border-neutral-200 bg-white py-2.5 pl-10 text-sm text-at-dark outline-none placeholder:text-neutral-400 focus:border-at-gold focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-at-gold ${query ? "pr-10" : "pr-4"}`}
                    />
                    {query ? (
                      <button
                        type="button"
                        aria-label="Clear search"
                        onClick={() => setQueryValue("")}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-neutral-400 hover:text-at-dark focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-at-gold"
                      >
                        <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                          <path d="M6 6l12 12M18 6L6 18" />
                        </svg>
                      </button>
                    ) : null}
                  </label>
                  <label className="relative block w-full sm:w-48">
                    <span className="sr-only">Filter by category</span>
                    <select
                      value={categoryFilterEnabled ? categorySlug : ""}
                      disabled={!categoryFilterEnabled}
                      onChange={(event) => setCategory(event.target.value)}
                      className="w-full appearance-none rounded-full border border-neutral-200 bg-white py-2.5 pl-4 pr-10 text-sm text-at-dark outline-none focus:border-at-gold focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-at-gold disabled:cursor-not-allowed disabled:opacity-50"
                    >
                      <option value="">All Categories</option>
                      {categories.map((item) => (
                        <option key={item._id || item.slug} value={item.slug}>
                          {item.categoryName}
                        </option>
                      ))}
                    </select>
                    <svg
                      viewBox="0 0 24 24"
                      className="pointer-events-none absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-neutral-400"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      aria-hidden="true"
                    >
                      <path d="M6 9l6 6 6-6" />
                    </svg>
                  </label>
                  <label className="relative block w-full sm:w-48">
                    <span className="sr-only">Sort products</span>
                    <select
                      value={sort}
                      disabled={productStatus !== "ready"}
                      onChange={(event) => setSortValue(event.target.value)}
                      className="w-full appearance-none rounded-full border border-neutral-200 bg-white py-2.5 pl-4 pr-10 text-sm text-at-dark outline-none focus:border-at-gold focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-at-gold disabled:cursor-not-allowed disabled:opacity-50"
                    >
                      <option value="newest">Sort by: Newest</option>
                      <option value="oldest">Sort by: Oldest</option>
                      <option value="name-asc">Sort by: Name A–Z</option>
                      <option value="name-desc">Sort by: Name Z–A</option>
                    </select>
                    <svg
                      viewBox="0 0 24 24"
                      className="pointer-events-none absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-neutral-400"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      aria-hidden="true"
                    >
                      <path d="M6 9l6 6 6-6" />
                    </svg>
                  </label>
                </>
              )}
            </div>
          </div>

          <div className="mt-8" aria-busy={productStatus === "loading" || pageBusy}>
            {productStatus === "loading" ? (
              <div className={MEDIA_CARD_GRID} aria-hidden="true">
                {Array.from({ length: PAGE_SIZE }).map((_, index) => (
                  <div key={index} className={`bg-neutral-200 ${MEDIA_CARD_CLASS}`} />
                ))}
              </div>
            ) : null}

            {productStatus === "error" ? (
              <div className="text-sm text-neutral-500">
                <p>Unable to load products.</p>
                <button
                  type="button"
                  onClick={(event) => {
                    lockDoubleClick(event);
                    setLoadNonce((value) => value + 1);
                  }}
                  className="hero-btn mt-3 font-semibold text-at-dark underline decoration-at-gold underline-offset-4 hover:text-at-dark focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-at-gold"
                >
                  Retry
                </button>
              </div>
            ) : null}

            {zeroProducts ? (
              <div className="max-w-xl">
                <h3 className="font-serif text-2xl uppercase text-at-dark">NO PRODUCTS AVAILABLE YET</h3>
                <p className="mt-3 text-sm text-neutral-500">
                  Products will be added soon. Contact our team if you need help sourcing an item.
                </p>
                <div className="mt-6 flex flex-wrap gap-3">
                  <Link
                    to="/category"
                    onClick={lockDoubleClick}
                    className="hero-btn inline-flex items-center gap-2 rounded-sm bg-at-gold px-5 py-2.5 text-sm font-semibold text-at-dark hover:bg-at-gold-hover"
                  >
                    Browse Categories <span className="btn-arrow">→</span>
                  </Link>
                  <Link
                    to="/contact"
                    onClick={lockDoubleClick}
                    className="hero-btn inline-flex items-center gap-2 rounded-sm border border-neutral-300 px-5 py-2.5 text-sm font-semibold text-at-dark hover:border-at-gold"
                  >
                    Contact Our Team <span className="btn-arrow">→</span>
                  </Link>
                </div>
              </div>
            ) : null}

            {noFilterResults ? (
              <div className="max-w-xl">
                <h3 className="font-serif text-2xl uppercase text-at-dark">NO MATCHING PRODUCTS</h3>
                <p className="mt-3 text-sm text-neutral-500">Try changing your search term or category filter.</p>
                <div className="mt-6 flex flex-wrap gap-3">
                  <button
                    type="button"
                    onClick={(event) => {
                      lockDoubleClick(event);
                      clearFilters();
                    }}
                    className="hero-btn inline-flex items-center gap-2 rounded-sm bg-at-gold px-5 py-2.5 text-sm font-semibold text-at-dark hover:bg-at-gold-hover"
                  >
                    Clear Filters <span className="btn-arrow">→</span>
                  </button>
                  <Link
                    to="/contact?intent=quote#contact-form"
                    onClick={lockDoubleClick}
                    className="hero-btn inline-flex items-center gap-2 rounded-sm border border-neutral-300 px-5 py-2.5 text-sm font-semibold text-at-dark hover:border-at-gold"
                  >
                    Request a Quote <span className="btn-arrow">→</span>
                  </Link>
                </div>
              </div>
            ) : null}

            {productStatus === "ready" && visible.length ? (
              <div
                className={`product-results ${MEDIA_CARD_GRID} ${pageBusy ? "is-updating" : ""}`}
              >
                {visible.map((item, index) => (
                  <CategoryCard
                    key={item._id || item.slug}
                    to={productHref(item)}
                    photo={publicMediaSrc(item.image)}
                    alt={productCardImageAlt(item, contact)}
                    title={item.productName}
                    description={categoryNameOf(item)}
                    lazy={index >= 4}
                    className={MEDIA_CARD_CLASS}
                  />
                ))}
              </div>
            ) : null}
          </div>

          {productStatus === "ready" && filtered.length > PAGE_SIZE ? (
            <nav aria-label="Pagination" className="mt-10 flex items-center justify-center gap-3 text-sm text-neutral-500">
              <button
                type="button"
                disabled={pageBusy || currentPage <= 1}
                onClick={() => goToPage(currentPage - 1)}
                className="disabled:opacity-40 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-at-gold"
              >
                Previous
              </button>
              {Array.from({ length: pageCount }).map((_, index) => {
                const number = index + 1;
                const active = number === currentPage;
                return (
                  <button
                    key={number}
                    type="button"
                    disabled={pageBusy}
                    aria-current={active ? "page" : undefined}
                    onClick={() => goToPage(number)}
                    className={`flex h-8 w-8 items-center justify-center rounded-full focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-at-gold ${active ? "bg-at-gold text-at-dark" : "hover:text-at-dark"}`}
                  >
                    {number}
                  </button>
                );
              })}
              <button
                type="button"
                disabled={pageBusy || currentPage >= pageCount}
                onClick={() => goToPage(currentPage + 1)}
                className="disabled:opacity-40 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-at-gold"
              >
                Next
              </button>
            </nav>
          ) : null}
        </div>
      </section>
    </div>
  );
}

export default ProductsPage;
